import { useRef, type CSSProperties } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
  type MotionValue,
} from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSite } from '../content';

interface ParallaxWindowProps {
  imageUrl: string;
  alt: string;
  style: CSSProperties;
  scrollYProgress: MotionValue<number>;
  speed: number;
  objectPosition?: string;
  objectFit?: 'cover' | 'contain';
  topFade?: boolean;
}

function ParallaxWindow({
  imageUrl,
  alt,
  style,
  scrollYProgress,
  speed,
  objectPosition = 'center center',
  objectFit = 'cover',
  topFade = false,
}: ParallaxWindowProps) {
  const prefersReducedMotion = useReducedMotion();
  const effectiveSpeed = prefersReducedMotion ? 0 : speed;
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [`${effectiveSpeed * 25}%`, `${-effectiveSpeed * 25}%`],
  );

  if (objectFit === 'contain') {
    return (
      <motion.div
        style={{
          position: 'absolute',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style,
        }}
        initial={prefersReducedMotion ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: prefersReducedMotion ? 0 : 1.2 }}
      >
        <img
          src={imageUrl}
          alt={alt}
          style={{ objectPosition }}
          className="w-full h-full object-contain"
        />
      </motion.div>
    );
  }

  const buffer = effectiveSpeed * 25;
  return (
    <motion.div
      style={{ position: 'absolute', overflow: 'hidden', ...style }}
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: prefersReducedMotion ? 0 : 1.2 }}
    >
      <motion.img
        src={imageUrl}
        alt={alt}
        style={{
          y: effectiveSpeed === 0 ? 0 : y,
          objectPosition,
          position: 'absolute',
          left: 0,
          right: 0,
          top: `-${buffer}%`,
          bottom: `-${buffer}%`,
          width: '100%',
          height: `${100 + buffer * 2}%`,
          objectFit: 'cover',
        }}
      />
      {topFade && (
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 w-full h-32 pointer-events-none z-10"
          style={{
            background: 'linear-gradient(to bottom, #2A2420 0%, transparent 100%)',
          }}
        />
      )}
    </motion.div>
  );
}

export function SlateGallery() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const site = useSite();
  const { gallery } = site;
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const trackingSpring = { stiffness: 1000, damping: 200, mass: 0.1 };
  const landingSpring = { stiffness: 400, damping: 45, mass: 0.3 };

  const textOneOpacityRaw = useTransform(scrollYProgress, [0.22, 0.26], [0, 1]);
  const textOneOpacity = useSpring(textOneOpacityRaw, trackingSpring);
  const textOneYRaw = useTransform(
    scrollYProgress,
    [0.22, 0.24, 0.26, 0.272, 0.278, 0.284, 1.0],
    [-214, -150, -80, -35, -10, 0, 0],
  );
  const textOneY = useSpring(textOneYRaw, landingSpring);

  const textTwoOpacityRaw = useTransform(scrollYProgress, [0.57, 0.61], [0, 1]);
  const textTwoOpacity = useSpring(textTwoOpacityRaw, trackingSpring);
  const textTwoYRaw = useTransform(
    scrollYProgress,
    [0.57, 0.585, 0.6, 0.61, 0.618, 0.624, 1.0],
    [-179, -130, -70, -30, -8, 0, 0],
  );
  const textTwoY = useSpring(textTwoYRaw, landingSpring);

  const img1Height = isMobile ? '350px' : '680px';
  const img1Width = isMobile ? '92%' : '84%';
  const img2Top = isMobile ? 'calc(8vh + 350px + 100px)' : 'calc(8vh + 680px + 140px)';
  const img2Width = isMobile ? '90%' : '85%';
  const img3Top = isMobile ? 'calc(8vh + 950px)' : 'calc(8vh + 1620px)';
  const img3Width = isMobile ? '75%' : '58%';
  const img4Top = isMobile ? 'calc(8vh + 1300px)' : 'calc(8vh + 2200px)';
  const img4Width = isMobile ? '88%' : '72%';
  const containerHeight = isMobile ? 'calc(8vh + 1800px)' : 'calc(8vh + 3000px)';
  const text1Top = isMobile ? 'calc(8vh + 380px)' : 'calc(8vh + 722px)';
  const text2Top = isMobile ? 'calc(8vh + 1220px)' : 'calc(8vh + 2125px)';
  const textFontSize = isMobile ? '28px' : '55px';
  const textLeftRight = isMobile ? '10%' : '22%';

  const [img1, img2, img3, img4] = gallery.images;

  return (
    <section
      ref={containerRef}
      aria-label={`Gallery: ${gallery.textOne}, ${gallery.textTwo}`}
      className="relative w-full overflow-hidden"
      style={{ height: containerHeight }}
    >
      {/* Decorative layers span the full viewport width */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-0 w-full h-48 md:h-64 z-30 bg-gradient-to-b from-[#2A2420] to-transparent pointer-events-none"
      />

      <div aria-hidden="true" className="absolute inset-0 z-10 pointer-events-none">
        <img
          src={gallery.textureImageUrl}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2A2420]/75" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1816]/30 to-[#2A2420]/40" />
      </div>

      {/* Positioned content stays within a 1200px column so the
          percentage-based layout doesn't drift on wider viewports */}
      <div className="relative mx-auto h-full max-w-[1200px]">
        <motion.p
          className="absolute z-50 font-serif font-light tracking-[0.01em] text-white/80 pointer-events-none"
          style={{
            top: text1Top,
            left: textLeftRight,
            fontSize: textFontSize,
            lineHeight: 1,
            opacity: prefersReducedMotion ? 1 : textOneOpacity,
            y: prefersReducedMotion ? 0 : textOneY,
            textShadow: '0 2px 12px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)',
          }}
        >
          {gallery.textOne}
        </motion.p>

        <motion.p
          className="absolute z-50 font-serif font-light tracking-[0.01em] text-white/80 pointer-events-none"
          style={{
            top: text2Top,
            right: textLeftRight,
            fontSize: textFontSize,
            lineHeight: 1,
            opacity: prefersReducedMotion ? 1 : textTwoOpacity,
            y: prefersReducedMotion ? 0 : textTwoY,
            textShadow: '0 2px 12px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)',
          }}
        >
          {gallery.textTwo}
        </motion.p>

        <div className="relative z-20 w-full h-full">
        {img1 && (
          <ParallaxWindow
            imageUrl={img1.url}
            alt={img1.alt}
            objectPosition="center bottom"
            topFade
            style={{
              top: '8vh',
              right: '0%',
              width: img1Width,
              height: img1Height,
              boxShadow: '0 0 50px rgba(0,0,0,0.65)',
              zIndex: 10,
            }}
            scrollYProgress={scrollYProgress}
            speed={isMobile ? 0.3 : 0.8}
          />
        )}

        {img2 && (
          <ParallaxWindow
            imageUrl={img2.url}
            alt={img2.alt}
            objectPosition="72% 65%"
            style={{
              top: img2Top,
              left: '0%',
              width: img2Width,
              aspectRatio: '4/3',
              boxShadow: '0 0 40px rgba(0,0,0,0.6)',
              zIndex: 20,
            }}
            scrollYProgress={scrollYProgress}
            speed={isMobile ? 0.3 : 0.8}
          />
        )}

        {img3 && (
          <ParallaxWindow
            imageUrl={img3.url}
            alt={img3.alt}
            objectFit="contain"
            style={{
              top: img3Top,
              right: '4%',
              width: img3Width,
              aspectRatio: '3/2',
              boxShadow: '0 0 50px rgba(0,0,0,0.7)',
              zIndex: 30,
              background: 'transparent',
            }}
            scrollYProgress={scrollYProgress}
            speed={0}
          />
        )}

        {img4 && (
          <ParallaxWindow
            imageUrl={img4.url}
            alt={img4.alt}
            style={{
              top: img4Top,
              left: isMobile ? '5%' : '10%',
              width: img4Width,
              aspectRatio: '16/9',
              boxShadow: '0 0 40px rgba(0,0,0,0.6)',
              zIndex: 10,
            }}
            scrollYProgress={scrollYProgress}
            speed={isMobile ? 0.2 : 0.5}
          />
        )}
        </div>
      </div>
    </section>
  );
}
